import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X, Clock } from 'lucide-react'
import { searchCatalogue, type SearchResult } from '@/lib/api'
import { useDebounce } from '@/hooks/useDebounce'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Image } from '@/components/ui/Image'
import { formatPrice } from '@/lib/utils'

const RECENT_KEY = 'little-sky-recent-searches'
const MAX_RECENT = 8

interface InlineSearchProps {
  onOpenOverlay?: () => void
  navColor?: string
  scrolled: boolean
}

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function addRecent(term: string) {
  if (!term.trim()) return
  const next = [term, ...(loadRecent().filter((t) => t !== term))].slice(0, MAX_RECENT)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {}
}

export function InlineSearch({ onOpenOverlay, navColor, scrolled }: InlineSearchProps) {
  const [expanded, setExpanded] = useState(false)
  const [query, setQuery] = useState('')
  const debounced = useDebounce(query, 220)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [recent, setRecent] = useState<string[]>([])
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isNarrow = useMediaQuery('(max-width: 767px)')

  const close = useCallback(() => {
    setExpanded(false)
    setQuery('')
    setResults([])
  }, [])

  useEffect(() => {
    close()
  }, [pathname, close])

  useEffect(() => {
    if (!expanded) return
    setRecent(loadRecent())
    const t = window.setTimeout(() => inputRef.current?.focus(), 60)
    return () => window.clearTimeout(t)
  }, [expanded])

  useEffect(() => {
    if (!expanded) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onDown)
    }
  }, [expanded, close])

  useEffect(() => {
    if (!debounced.trim()) {
      setResults([])
      return
    }
    setLoading(true)
    searchCatalogue(debounced, 8)
      .then((items) => setResults(items))
      .catch(() => setResults([]))
      .finally(() => setLoading(false))
  }, [debounced])

  const handleTrigger = () => {
    if (isNarrow) {
      onOpenOverlay?.()
      return
    }
    setExpanded((v) => !v)
  }

  const goToProduct = (slug: string) => {
    addRecent(query)
    close()
    navigate(`/product/${slug}`)
  }

  const submitSearch = (q: string) => {
    if (!q.trim()) return
    addRecent(q)
    close()
    navigate(`/shop?search=${encodeURIComponent(q)}`)
  }

  const pillMaterial = scrolled
    ? 'bg-paper/80 backdrop-blur-xl shadow-sticky'
    : 'bg-white/40 backdrop-blur-xl'

  return (
    <div ref={rootRef} className="relative flex items-center">
      <div
        className={`flex items-center rounded-full px-1.5 py-1 transition-all duration-300 ease-out-expo ${
          expanded ? pillMaterial : ''
        }`}
      >
        <button
          type="button"
          aria-label={expanded ? 'Search products' : 'Search'}
          aria-expanded={expanded}
          onClick={handleTrigger}
          className={`group relative grid h-9 w-9 place-items-center rounded-full transition-colors ${navColor ?? 'text-carbon'}`}
        >
          <Search className="h-[18px] w-[18px]" strokeWidth={1.6} />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-ink/10 opacity-0 blur-md transition-opacity duration-500 ease-out group-hover:opacity-100"
          />
        </button>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="field"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 220, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 420, damping: 38 }}
              className="overflow-hidden"
            >
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSearch(query)
                }}
                placeholder="Search fragrances..."
                aria-label="Search products"
                className="w-full bg-transparent px-2 text-sm text-carbon placeholder:text-carbon/40 outline-none"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {expanded && (
          <button
            type="button"
            aria-label="Close search"
            onClick={close}
            className={`grid h-9 w-9 place-items-center rounded-full transition-colors ${navColor ?? 'text-carbon'}`}
          >
            <X className="h-4 w-4" strokeWidth={1.6} />
          </button>
        )}
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-[calc(100%+10px)] w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-paper shadow-float"
          >
            {!debounced.trim() && recent.length > 0 && (
              <div className="border-b border-ink/5 px-4 py-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-wd-orange">
                  Recent searches
                </p>
                <div className="flex flex-wrap gap-2">
                  {recent.map((term) => (
                    <button
                      key={term}
                      onClick={() => submitSearch(term)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-3 py-1.5 text-xs text-carbon transition-colors hover:bg-oyster"
                    >
                      <Clock className="h-3 w-3 text-wd-orange" />
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {debounced.trim() && (
              <div className="max-h-[60vh] overflow-y-auto py-1">
                {loading ? (
                  <div className="space-y-3 px-4 py-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="h-12 w-12 bg-stone" />
                        <div className="flex-1 space-y-2 py-1">
                          <div className="h-3 w-2/3 bg-stone" />
                          <div className="h-2 w-1/2 bg-stone" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : results.length === 0 ? (
                  <p className="px-5 py-5 text-sm text-carbon/50">
                    No results for “{debounced}”.
                  </p>
                ) : (
                  <ul>
                    {results.map((p) => (
                      <li key={p.slug}>
                        <button
                          onClick={() => goToProduct(p.slug)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-oyster"
                        >
                          <Image
                            src={p.image}
                            alt=""
                            aspectRatio="1/1"
                            className="h-11 w-11 shrink-0 rounded-lg object-cover"
                            containerClassName="h-11 w-11 shrink-0"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-carbon">
                              {p.name}
                            </span>
                            <span className="block text-xs uppercase tracking-[0.15em] text-wd-orange">
                              {p.family}
                            </span>
                          </span>
                          <span className="shrink-0 text-xs text-carbon/70">
                            {formatPrice(p.price)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
